import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../state/store';
import { fetchData } from '../../services/apiClient';
import { API_ENDPOINTS } from '../../services/ApiConfig';

export interface AuthState {
  username: string | null;
  accessToken: string | null;
  loading: boolean;
  error: string | null;
  loginDetails: any | null;
  isTokenExpire: boolean;
  isLoginProgress: boolean;
  videoURL: string;
  videoURLoader: boolean;
  videoURLError: string | null;
}

const loadAuthStateFromCookies = (): AuthState => {
  const getCookieValue = (name: string) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  };

  const username = getCookieValue('username');
  const accessToken = getCookieValue('accessToken');

  return {
    username: username || null,
    accessToken: accessToken || null,
    loading: false,
    error: null,
    loginDetails: null,
    isTokenExpire: false,
    isLoginProgress: false,
    videoURL: '',
    videoURLError: null,
    videoURLoader: false,
  };
};

const initialState: AuthState = loadAuthStateFromCookies();

export const fetchUserLoginDetailsAPI = async (payload: any) => {
  return fetchData<any>(API_ENDPOINTS.AUTH.FETCH_OWNER_LAST_LOGIN_DETAILS, payload);
};

export const fetchUserLoginDetails = createAsyncThunk<any, any, { rejectValue: string }>(
  'auth/fetchUserLoginDetails',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await fetchUserLoginDetailsAPI(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  },
);

export const fetchUserGuideVideoAPI = async (payload: any) => {
  return fetchData<any>(API_ENDPOINTS.AUTH.VIDEO_URL, payload);
};

export const fetchUserGuideVideo = createAsyncThunk<any, any, { rejectValue: string }>(
  'auth/fetchUserGuideVideo',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await fetchUserGuideVideoAPI(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthState: (state, action) => {
      state.username = action.payload.username;
      state.accessToken = action.payload.accessToken;
    },
    clearAuthState: (state) => {
      state.username = null;
      state.accessToken = null;
      document.cookie = 'username=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    },
    setIsTokenExpire: (state, action) => {
      state.isTokenExpire = action.payload;
    },
    setIsLoginProgress: (state, action) => {
      state.isLoginProgress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserLoginDetails.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUserLoginDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.loginDetails = action.payload;
      state.error = null;
    });
    builder.addCase(fetchUserLoginDetails.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'An error occurred';
    });

    builder.addCase(fetchUserGuideVideo.pending, (state) => {
      state.videoURLoader = true;
      state.videoURLError = null;
    });
    builder.addCase(fetchUserGuideVideo.fulfilled, (state, action) => {
      state.videoURLoader = false;
      state.videoURL = action.payload;
      state.videoURLError = null;
    });
    builder.addCase(fetchUserGuideVideo.rejected, (state, action) => {
      state.videoURLoader = false;
      state.videoURLError = action.payload || 'An error occurred';
    });
  },
});

export const selectAuthUsername = (state: RootState) => state.auth.username;
export const selectAuthAccessToken = (state: RootState) => state.auth.accessToken;
export const selectIsTokenExpire = (state: RootState) => state.auth.isTokenExpire;
export const selectIsLoginProgress = (state: RootState) => state.auth.isLoginProgress;
export const selectAuthLoading = (state: RootState) => state.auth.loading;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectVideoURLLoading = (state: RootState) => state.auth.videoURLoader;
export const selectVideoURLError = (state: RootState) => state.auth.videoURLError;
export const selectVideURL = (state: RootState) => state.auth.videoURL;
export const selectAuthLoginDetails = (state: RootState) => state.auth.loginDetails;

export const { setAuthState, clearAuthState, setIsTokenExpire, setIsLoginProgress } = authSlice.actions;

export default authSlice.reducer;
